'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Star, Wifi, Zap, MapPin } from 'lucide-react';
import type { Cafe } from '@/data/mockCafes';

interface Props {
  cafes: Cafe[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function CafeListPanel({ cafes, selectedId, onSelect }: Props) {
  if (cafes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-4xl mb-3">☕</div>
        <p className="text-base font-700 text-primary mb-1">Belum ada cafe di sini</p>
        <p className="text-sm text-muted-foreground">Belum ada hidden gems di area ini.. Mungkin kamu yang pertama nemuin?</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-8">
      {cafes.map((cafe) => (
        <div
          key={`list-${cafe.id}`}
          onClick={() => onSelect(cafe.id)}
          className={`flex gap-3 p-3 rounded-2xl border cursor-pointer transition-all ${
            selectedId === cafe.id
              ? 'border-accent bg-accent/5 shadow-card'
              : 'border-border bg-card hover:border-accent/40 hover:shadow-card'
          }`}
        >
          {/* Thumbnail */}
          <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
            <AppImage
              src={cafe.photos[0]}
              alt={`Foto ${cafe.name}, cafe di ${cafe.area}`}
              fill
              className="object-cover"
              sizes="80px"
            />
            <div className="absolute top-1 left-1">
              <span className={cafe.isOpen ? 'open-badge' : 'closed-badge'} style={{ fontSize: '9px', padding: '2px 6px' }}>
                {cafe.isOpen ? 'Buka' : 'Tutup'}
              </span>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <Link
                href={`/coffeshop-detail-page?id=${cafe.id}`}
                className="text-sm font-700 text-foreground hover:text-accent transition-colors line-clamp-1"
                onClick={(e) => e.stopPropagation()}
              >
                {cafe.name}
              </Link>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Star size={11} className="rating-star fill-current" />
                <span className="text-xs font-700 text-foreground font-tabular">{cafe.rating}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-muted-foreground">{cafe.area}</span>
              <span className="text-muted-foreground text-xs mx-1">·</span>
              <span className="text-xs text-muted-foreground font-tabular">
                Rp{(cafe.priceMin / 1000).toFixed(0)}K–{(cafe.priceMax / 1000).toFixed(0)}K
              </span>
            </div>

            <div className="flex items-center gap-2 mt-2">
              {cafe.wifi && (
                <span className="flex items-center gap-1 text-[10px] text-green-DEFAULT font-500">
                  <Wifi size={10} /> WiFi
                </span>
              )}
              {cafe.outlet && (
                <span className="flex items-center gap-1 text-[10px] text-green-DEFAULT font-500">
                  <Zap size={10} /> Colokan
                </span>
              )}
            </div>

            <div className="flex gap-1 mt-1.5 overflow-hidden">
              {cafe.vibeTags.slice(0, 3).map((tag) => (
                <span key={`ltag-${cafe.id}-${tag}`} className="tag-pill text-[9px] py-0.5 px-2">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}